/**
 * API Client
 * Centralized fetch wrapper with authentication and error handling
 */

import { apiUrl } from '../../config';

/**
 * Base API client with authentication
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
export const apiClient = async (endpoint, options = {}) => {
  const token = localStorage.getItem('accessToken');

  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(apiUrl(endpoint), config);

    // Handle non-OK responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Detect expired/invalid token and notify the app globally
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
      }

      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error.message);
    throw error;
  }
};

/**
 * GET request
 */
export const get = (endpoint, options = {}) => {
  return apiClient(endpoint, {
    method: 'GET',
    ...options,
  });
};

/**
 * POST request
 */
export const post = (endpoint, data, options = {}) => {
  return apiClient(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options,
  });
};

/**
 * PUT request
 */
export const put = (endpoint, data, options = {}) => {
  return apiClient(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options,
  });
};

/**
 * PATCH request
 */
export const patch = (endpoint, data, options = {}) => {
  return apiClient(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
    ...options,
  });
};

/**
 * DELETE request
 */
export const del = (endpoint, options = {}) => {
  return apiClient(endpoint, {
    method: 'DELETE',
    ...options,
  });
};

/**
 * POST with FormData (for file uploads)
 * @param {string} endpoint - API endpoint path
 * @param {FormData} formData - Form data to send
 * @param {AbortSignal} [signal] - Optional AbortSignal for cancellation
 * @param {Object} [options] - Additional fetch options
 */
export const postFormData = async (endpoint, formData, signal, options = {}) => {
  const token = localStorage.getItem('accessToken');

  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(apiUrl(endpoint), {
    method: 'POST',
    headers: {
      ...headers,
      ...options.headers,
    },
    body: formData,
    signal,
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Detect expired/invalid token and notify the app globally
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:token-expired'));
    }

    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  return response.json();
};
